import { Add, DeleteOutline, EditOutlined, RemoveRedEyeOutlined, Search } from '@mui/icons-material';
import {
    Alert,
    Button,
    ButtonGroup,
    Card,
    CircularProgress,
    Container,
    IconButton,
    InputAdornment,
    Stack,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TablePagination,
    TableRow,
    TextField,
    Typography,
} from '@mui/material';
import { useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';

import PageTitle from '../components/PageTitle';
import { GeneralLayout } from '../layouts/GeneralLayout';
import {
    useDeleteDocumentFromRegistryMutation,
    useGetRegistryDocumentsQuery,
} from '../store/document/documentApi';

const PAGE_SIZE_OPTIONS = [25, 50, 100];

export default function RegistryOfDocumentsPage() {
    const navigate = useNavigate();
    const [page, setPage] = useState(0);
    const [pageSize, setPageSize] = useState(25);
    const [search, setSearch] = useState('');
    const [debouncedSearch, setDebouncedSearch] = useState('');
    const { data, isLoading, isFetching, isError } = useGetRegistryDocumentsQuery({
        page: page + 1,
        pageSize,
        search: debouncedSearch,
    });
    const [deleteDocument] = useDeleteDocumentFromRegistryMutation();
    const [deletingDocumentId, setDeletingDocumentId] = useState(null);
    const fileLinks = useRef([]);
    const registry = data?.data;
    const documents = registry?.items ?? [];

    useEffect(() => {
        const timeout = setTimeout(() => setDebouncedSearch(search), 300);
        return () => clearTimeout(timeout);
    }, [search]);

    useEffect(() => {
        setPage(0);
    }, [debouncedSearch]);

    function toDateString(date) {
        return date ? new Date(date).toLocaleDateString('ru-RU') : '';
    }

    function openRegistryDocument(link) {
        const cachedLink = fileLinks.current.find((item) => item.link === link);
        if (cachedLink) {
            window.open(cachedLink.correctLink, '_blank');
            return;
        }

        fetch(link)
            .then((response) => {
                const correctLink = response.status === 404 ? `${link}?useTest=1` : link;
                fileLinks.current.push({ link, correctLink });
                window.open(correctLink, '_blank');
            })
            .catch(() => {
                const correctLink = `${link}?useTest=1`;
                fileLinks.current.push({ link, correctLink });
                window.open(correctLink, '_blank');
            });
    }

    async function handleDelete(document) {
        const confirmed = window.confirm(
            `Удалить документ «${document.documentNumber}» для ${document.studentFullName}?`
        );
        if (!confirmed) {
            return;
        }

        setDeletingDocumentId(document.registryDocumentId);
        try {
            const response = await deleteDocument({ documentId: document.registryDocumentId }).unwrap();
            if (!response?.success) {
                alert(response?.errors?.join('\n') ?? 'Не удалось удалить документ');
            }
        } catch (error) {
            alert(error?.data?.errors?.join('\n') ?? 'Не удалось удалить документ');
        } finally {
            setDeletingDocumentId(null);
        }
    }

    return (
        <GeneralLayout>
            <PageTitle title="Реестр документов" />

            <Container>
                <Stack direction="column" padding="120px 0 0 100px" spacing={2}>
                    <Stack direction="row" alignItems="center" justifyContent="space-between" mb={5}>
                        <Typography variant="h4" gutterBottom>
                            Реестр документов
                        </Typography>
                        <Stack direction="row" alignItems="center" spacing={2}>
                            {isFetching && !isLoading && <CircularProgress size={20} />}
                            <Button
                                variant="contained"
                                startIcon={<Add />}
                                onClick={() => navigate('/create-document-in-registry')}
                            >
                                Добавить новый
                            </Button>
                        </Stack>
                    </Stack>

                    <TextField
                        fullWidth
                        label="Поиск"
                        placeholder="ФИО студента, № документа или дата (дд.мм.гггг)"
                        value={search}
                        onChange={(event) => setSearch(event.target.value)}
                        InputProps={{
                            startAdornment: (
                                <InputAdornment position="start">
                                    <Search color="action" />
                                </InputAdornment>
                            ),
                        }}
                    />

                    {isError && <Alert severity="error">Не удалось загрузить реестр документов.</Alert>}

                    <Card>
                        {isLoading ? (
                            <Stack alignItems="center" padding={3}>
                                <CircularProgress />
                            </Stack>
                        ) : (
                            <>
                                <TableContainer>
                                    <Table size="small">
                                        <TableHead>
                                            <TableRow>
                                                <TableCell>#</TableCell>
                                                <TableCell>№ документа</TableCell>
                                                <TableCell>Дата документа</TableCell>
                                                <TableCell>Семестры</TableCell>
                                                <TableCell>Студент</TableCell>
                                                <TableCell>Действия</TableCell>
                                            </TableRow>
                                        </TableHead>
                                        <TableBody>
                                            {documents.map((item, index) => (
                                                <TableRow key={item.registryDocumentId}>
                                                    <TableCell><strong>{page * pageSize + index + 1}</strong></TableCell>
                                                    <TableCell>{item.documentNumber}</TableCell>
                                                    <TableCell>{toDateString(item.documentDate)}</TableCell>
                                                    <TableCell>{item.semesters?.join(', ') ?? ''}</TableCell>
                                                    <TableCell>{item.studentFullName}</TableCell>
                                                    <TableCell>
                                                        <ButtonGroup>
                                                            <IconButton
                                                                title="Открыть документ"
                                                                onClick={() => openRegistryDocument(
                                                                    `https://fs.kubsau.ru/file/${item.registryDocumentGuid}`
                                                                )}
                                                            >
                                                                <RemoveRedEyeOutlined />
                                                            </IconButton>
                                                            <IconButton
                                                                title="Редактировать"
                                                                onClick={() => navigate(
                                                                    `/create-document-in-registry/${item.registryDocumentId}`
                                                                )}
                                                            >
                                                                <EditOutlined />
                                                            </IconButton>
                                                            <IconButton
                                                                title="Удалить"
                                                                color="error"
                                                                disabled={deletingDocumentId === item.registryDocumentId}
                                                                onClick={() => handleDelete(item)}
                                                            >
                                                                {deletingDocumentId === item.registryDocumentId
                                                                    ? <CircularProgress size={20} />
                                                                    : <DeleteOutline />}
                                                            </IconButton>
                                                        </ButtonGroup>
                                                    </TableCell>
                                                </TableRow>
                                            ))}
                                            {documents.length === 0 && !isError && (
                                                <TableRow>
                                                    <TableCell colSpan={6} align="center" sx={{ py: 4 }}>
                                                        <Typography color="text.secondary">Документы не найдены.</Typography>
                                                    </TableCell>
                                                </TableRow>
                                            )}
                                        </TableBody>
                                    </Table>
                                </TableContainer>
                                <TablePagination
                                    component="div"
                                    count={registry?.totalCount ?? 0}
                                    page={page}
                                    onPageChange={(_, nextPage) => setPage(nextPage)}
                                    rowsPerPage={pageSize}
                                    onRowsPerPageChange={(event) => {
                                        setPageSize(Number(event.target.value));
                                        setPage(0);
                                    }}
                                    rowsPerPageOptions={PAGE_SIZE_OPTIONS}
                                    labelRowsPerPage="Строк на странице:"
                                    labelDisplayedRows={({ from, to, count }) => `${from}–${to} из ${count}`}
                                />
                            </>
                        )}
                    </Card>
                    
                    <br />
                </Stack>
            </Container>
        </GeneralLayout>
    );
}
